import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, BehaviorSubject } from 'rxjs';
import { jwtDecode, JwtPayload } from "jwt-decode";
import { SharedDataService } from '../centraliced-routes-cards.service';
import { Card, Button } from '../../model/card-buttons.model';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}

interface MyKeycloakTokenPayload extends JwtPayload {
  groups?: string[];
  realm_access?: { roles: string[]; };
  resource_access?: any;
  email?: string;
  name?: string;
  displayName?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  'allowed-origins'?: string[];
  azp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class KeycloakAuthService {
  private readonly tokenUrl = `${environment.ssoUrl}/realms/${environment.ssoRealm}/protocol/openid-connect/token`;
  private readonly logoutUrl = `${environment.ssoUrl}/realms/${environment.ssoRealm}/protocol/openid-connect/logout`;
  private readonly ADMIN_GROUP = 'grpGeddisAdmin';
  private readonly REQUIRED_GROUP = 'grpGeddis'; 

  private token: string | null = null;
  private refreshToken: string | null = null;
  private userGroups: string[] | undefined;
  private userEmail: string | undefined;
  private _userName: string | undefined;
  private _userLastName: string | undefined;
  private _displayName: string | undefined;

  private authenticatedSubject = new BehaviorSubject<boolean>(false);
  public authenticated$: Observable<boolean> = this.authenticatedSubject.asObservable();
  
  private authReadySubject = new BehaviorSubject<boolean>(false);
  public authReady$: Observable<boolean> = this.authReadySubject.asObservable();

  private userNameSubject = new BehaviorSubject<string | undefined>(undefined);
  public userName$: Observable<string | undefined> = this.userNameSubject.asObservable();
  private userLastNameSubject = new BehaviorSubject<string | undefined>(undefined);
  public userLastName$: Observable<string | undefined> = this.userLastNameSubject.asObservable();
  private displayNameSubject = new BehaviorSubject<string | undefined>(undefined);
  public displayName$: Observable<string | undefined> = this.displayNameSubject.asObservable();


  public get authorizedCards$(): Observable<Card[]> {
    return this.sharedDataService.authorizedCards$;
  }
  public get authorizedButtons$(): Observable<Button[]> {
    return this.sharedDataService.authorizedButtons$;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly sharedDataService: SharedDataService
  ) { }

  async init(): Promise<void> {
    const storedToken = sessionStorage.getItem('access_token');
    const storedRefreshToken = sessionStorage.getItem('refresh_token');

    if (storedToken && storedRefreshToken) {
      this.token = storedToken;
      this.refreshToken = storedRefreshToken;
      
      if (this.isTokenExpired(this.token)) {
        console.warn('Token expirado, se requiere nuevo login.');
        this.clearSession();
      } else {
        this.authenticatedSubject.next(true);
        this.processToken(this.token);
      }
    }
    
    this.authReadySubject.next(true);
  }

  async login(user: string, password: string): Promise<number> {
    const body = new HttpParams()
      .set('client_id', environment.ssoClientId)
      .set('username', user)
      .set('password', password)
      .set('grant_type', 'password');
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    try {
      const response = await firstValueFrom(this.http.post<LoginResponse>(this.tokenUrl, body.toString(), { headers }));
      
      this.token = response.access_token;
      this.refreshToken = response.refresh_token;

      sessionStorage.setItem('access_token', this.token);
      sessionStorage.setItem('refresh_token', this.refreshToken);

      this.authenticatedSubject.next(true);
      this.processToken(this.token);

      if(this.userGroups?.includes(this.REQUIRED_GROUP)) {
        const returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');
          this.router.navigate([returnUrl]);
        } else {
          this.router.navigate(['/Home']);
        }
        return 200;
      } else {
        this.clearSession();
        console.error("KeycloakAuthService: Error en login, usuario no autorizado.");
        return 403;
      }

    } catch (error: any) {
      this.clearSession();
      console.error("KeycloakAuthService: Error en login", error);
      return error.status || 500;
    }
  }

  async logout(): Promise<void> {
    const body = new HttpParams()
      .set('client_id', environment.ssoClientId)
      .set('refresh_token', this.refreshToken || '');
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    try {
        if (this.refreshToken) {
            await firstValueFrom(this.http.post(this.logoutUrl, body.toString(), { headers }));
        }
    } catch (err) {
        console.error('Error al enviar solicitud de logout a SSO:', err);
    } finally {
        this.clearSession();
        this.router.navigate(['/Login']);
    }
  }

  isLoggedIn(): boolean {
    return this.authenticatedSubject.getValue();
  }

  getToken(): string | null {
    return this.token;
  }

  getUserGroups(): string[] | undefined {
    return this.userGroups;
  }

  private processToken(token: string): void {
    const decodedToken: MyKeycloakTokenPayload = jwtDecode(token);
    this.userGroups = decodedToken.groups;
    this.userEmail = decodedToken.email;

    if (decodedToken.given_name && decodedToken.family_name) {
        this._displayName = decodedToken.displayName;
        this._userName = decodedToken.given_name;
        this._userLastName = decodedToken.family_name;
    } else {
        const extractedInfo = this.extraerNombreYApellidoDeEmail(this.userEmail);
        this._userName = extractedInfo?.nombre;
        this._userLastName = extractedInfo?.apellido;
    }

    this.userNameSubject.next(this._userName);
    this.userLastNameSubject.next(this._userLastName);
    this._displayName = decodedToken.displayName;
    this.displayNameSubject.next(this._displayName);
    
    this.loadAndFilterAuthorizedItems();
  }
  
  private clearSession(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');

    this.token = null;
    this.refreshToken = null;
    this.userGroups = undefined;
    this.userEmail = undefined;
    this._userName = undefined;
    this._userLastName = undefined;
    this._displayName = undefined;

    this.authenticatedSubject.next(false);
    this.userNameSubject.next(undefined);
    this.userLastNameSubject.next(undefined);
    this.displayNameSubject.next(undefined);
    
    this.sharedDataService.setAuthorizedCards([]);
    this.sharedDataService.setAuthorizedButtons([]);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decoded: JwtPayload = jwtDecode(token);
      if (typeof decoded.exp !== 'number') { return true; }
      const expirationDate = new Date(decoded.exp * 1000); // Conversión directa
      return expirationDate.valueOf() < Date.now();
    } catch {
      return true;
    }
  }
  
  private loadAndFilterAuthorizedItems(): void {
    const allCards = this.sharedDataService.allCards;
    const allButtons = this.sharedDataService.allButtons;
    const userGroups = this.getUserGroups();
    
    const filterByGroups = <T extends { requiredGroups?: string[] }>(items: T[]): T[] => {
      // Si el usuario es admin, devuelve todo.
      if (userGroups?.includes(this.ADMIN_GROUP)) {
          return items;
      }
      
      return items.filter(item => {
        // Si el item no requiere grupos, es visible para todos.
        if (!item.requiredGroups || item.requiredGroups.length === 0) {
            return true;
        }
        // Si el item requiere grupos pero el usuario no tiene, no es visible.
        if (!userGroups || userGroups.length === 0) {
            return false;
        }
        // Comprueba si el usuario tiene al menos uno de los grupos requeridos.
        return item.requiredGroups.some(requiredGroup =>
            userGroups.includes(requiredGroup)
        );
      });
    };

    const filteredCards = filterByGroups(allCards);
    const filteredButtons = filterByGroups(allButtons);

    this.sharedDataService.setAuthorizedCards(filteredCards);
    this.sharedDataService.setAuthorizedButtons(filteredButtons);
  }
  
  private extraerNombreYApellidoDeEmail(email: string | undefined): { nombre: string; apellido: string } | null {
    if (!email) { return null; }
    const localPart = email.split('@')[0]; 
    if (!localPart) { return null; }

    const nameParts = localPart.split('.').filter(part => part.length > 0);
    if (nameParts.length === 0) { return null; }

    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

    let nombre = '';
    let apellido = '';

    if (nameParts.length === 1) {
      nombre = capitalize(nameParts[0]);
    } else {
      apellido = capitalize(nameParts[nameParts.length - 1]);
      nombre = nameParts.slice(0, -1).map(capitalize).join(' ');
    }

    return { nombre, apellido };
  }
}